import {
  AdminPanelSettingsOutlined,
  CheckCircle,
  Close,
  KeyOutlined,
  PersonOutline,
  RadioButtonUnchecked,
  ShieldOutlined,
} from "@mui/icons-material";
import {
  alpha,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import api from "../../../../services/api";
import { useNotification } from "../../../../componentes/notificationProvider";
import { ACESSO_LABEL, MODULOS } from "../modalUserRoles";

const ADMIN_INFO = {
  adm001: {
    Icon: AdminPanelSettingsOutlined,
    description: "Acesso total ao sistema e à administração",
  },
  adm002: {
    Icon: ShieldOutlined,
    description: "Gerencia usuários, perfis e permissões, exceto Super Admin",
  },
};

const useStyles = (theme) => ({
  header: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    py: 1.5,
    px: 2,
    pr: 1,
  },
  headerIcon: {
    display: "flex",
    p: 0.75,
    borderRadius: "8px",
    color: "primary.main",
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
  },
  content: { display: "flex", flexDirection: "column", gap: 2, p: 2 },
  user: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    p: 1.5,
    borderRadius: "8px",
    backgroundColor: alpha(theme.palette.text.primary, 0.04),
  },
  sectionLabel: {
    fontSize: "11px",
    fontWeight: 600,
    color: "text.secondary",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    mb: 0.75,
  },
  group: { display: "flex", flexDirection: "column", gap: 1 },
  option: (selected) => ({
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    textAlign: "left",
    gap: 1.5,
    width: "100%",
    py: 1.25,
    px: 1.5,
    borderRadius: "8px",
    border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
    backgroundColor: selected
      ? alpha(theme.palette.primary.main, 0.08)
      : "transparent",
    transition: "all .15s",
    "&:hover": {
      borderColor: selected
        ? theme.palette.primary.main
        : theme.palette.text.secondary,
    },
    "&.Mui-focusVisible": {
      outline: `2px solid ${theme.palette.primary.main}`,
    },
    "&.Mui-disabled": { opacity: 0.5 },
  }),
  optionIcon: (selected) => ({
    display: "flex",
    p: 0.75,
    borderRadius: "6px",
    color: selected ? "primary.main" : "text.secondary",
    backgroundColor: alpha(
      selected ? theme.palette.primary.main : theme.palette.text.primary,
      selected ? 0.12 : 0.06,
    ),
  }),
  modulos: { display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 },
  modulo: (cor, nivel) => ({
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    px: 0.75,
    py: 0.25,
    borderRadius: "6px",
    whiteSpace: "nowrap",
    fontSize: "11.5px",
    fontWeight: 500,
    color: nivel === "none" ? "text.disabled" : "text.secondary",
    backgroundColor: alpha(theme.palette.text.primary, 0.05),
    "& svg": {
      fontSize: "15px",
      color: nivel === "none" ? theme.palette.text.disabled : cor,
    },
  }),
  indicator: (selected) => ({
    fontSize: "18px",
    flexShrink: 0,
    mt: 0.25,
    color: selected ? "primary.main" : "text.disabled",
  }),
  actions: { px: 2, py: 1.25, gap: 1 },
});

function nomeUsuario(row) {
  const perfil = row?.PerfilCliente;
  if (!perfil) return row?.email;
  return perfil.pessoaFisica
    ? [perfil.nome, perfil.sobrenome].filter(Boolean).join(" ")
    : perfil.nomeFantasia;
}

export default function ModalEditPermission({
  openEditPermissionModal,
  setOpenEditPermissionModal,
  selectedRow,
  setRows,
  role,
  permissoes = [],
}) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const notify = useNotification();
  const [permission, setPermission] = useState(null);
  const [loading, setLoading] = useState(false);
  const atual = selectedRow?.codigoPermissao;
  const isSuperAdmin = role === "adm001";
  const close = () => setOpenEditPermissionModal(false);

  useEffect(() => {
    if (openEditPermissionModal) setPermission(atual);
  }, [openEditPermissionModal, atual]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await api.put("/usuario/permissoes/update", {
        id: selectedRow.id,
        permissoes: permission,
        logsLoginClienteId: JSON.parse(localStorage.getItem("logs"))?.id || "",
      });
      const codigoPermissao = response.data?.codigoPermissao;
      if (codigoPermissao) {
        setRows((rows) =>
          rows.map((row) =>
            row?.id === selectedRow.id ? { ...row, codigoPermissao } : row,
          ),
        );
      }
      notify("Permissão atualizada com sucesso!", "success");
      close();
    } catch (error) {
      notify(
        error?.response?.data?.error || "Erro ao atualizar dados!",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const cor = (m) => m.color[theme.palette.mode];

  return (
    <Dialog
      open={openEditPermissionModal}
      onClose={close}
      fullWidth
      PaperProps={{ sx: { borderRadius: "10px", maxWidth: 540 } }}
    >
      <DialogTitle sx={styles.header}>
        <Box sx={styles.headerIcon}>
          <KeyOutlined fontSize="small" />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={600} fontSize="15px" lineHeight={1.3}>
            Alterar permissão
          </Typography>
          <Typography fontSize="12px" color="text.secondary">
            Escolha o perfil de acesso do usuário
          </Typography>
        </Box>
        <IconButton size="small" aria-label="Fechar" onClick={close}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent sx={styles.content}>
        <Box sx={styles.user}>
          <Avatar
            src={selectedRow?.PerfilCliente?.foto}
            sx={{ width: 40, height: 40 }}
          >
            {nomeUsuario(selectedRow)?.[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography fontSize="14px" fontWeight={600} noWrap>
              {nomeUsuario(selectedRow)}
            </Typography>
            <Typography fontSize="12px" color="text.secondary" noWrap>
              {selectedRow?.login}
            </Typography>
          </Box>
        </Box>

        {[
          {
            label: "Administração",
            items: permissoes.filter((p) => ADMIN_INFO[p.value]),
          },
          {
            label: "Usuário",
            items: permissoes.filter((p) => !ADMIN_INFO[p.value]),
          },
        ]
          .filter((grupo) => grupo.items.length)
          .map((grupo) => (
            <Box key={grupo.label}>
              <Typography sx={styles.sectionLabel}>{grupo.label}</Typography>
              <Box role="radiogroup" aria-label={grupo.label} sx={styles.group}>
                {grupo.items.map((p) => {
                  const selected = permission === p.value;
                  const admin = ADMIN_INFO[p.value];
                  const Icon = admin?.Icon || PersonOutline;
                  const Indicator = selected
                    ? CheckCircle
                    : RadioButtonUnchecked;
                  const bloqueado = p.value === "adm001" && !isSuperAdmin;
                  const option = (
                    <ButtonBase
                      key={p.value}
                      disabled={bloqueado}
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setPermission(p.value)}
                      sx={styles.option(selected)}
                    >
                      <Box sx={styles.optionIcon(selected)}>
                        <Icon fontSize="small" />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography fontSize="13px" fontWeight={600} noWrap>
                            {p.name}
                          </Typography>
                          {p.value === atual && (
                            <Chip
                              label="Atual"
                              size="small"
                              sx={{ height: 18, fontSize: "11px" }}
                            />
                          )}
                        </Box>
                        {admin ? (
                          <Typography fontSize="12px" color="text.secondary">
                            {admin.description}
                          </Typography>
                        ) : (
                          <Box sx={styles.modulos}>
                            {MODULOS.map((m) => {
                              const nivel = p.modulos?.[m.key] || "none";
                              return (
                                <Box
                                  key={m.key}
                                  sx={styles.modulo(cor(m), nivel)}
                                >
                                  <m.Icon />
                                  {m.label} · {ACESSO_LABEL[nivel]}
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                      <Indicator sx={styles.indicator(selected)} />
                    </ButtonBase>
                  );
                  return bloqueado ? (
                    <Tooltip
                      key={p.value}
                      title="Somente um Super Admin pode conceder este perfil"
                    >
                      <Box component="span" sx={{ display: "block" }}>
                        {option}
                      </Box>
                    </Tooltip>
                  ) : (
                    option
                  );
                })}
              </Box>
            </Box>
          ))}
      </DialogContent>

      <Divider />
      <DialogActions sx={styles.actions}>
        <Button onClick={close}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={!permission || permission === atual || loading}
          onClick={handleSave}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
